import { useCallback, useEffect, useMemo, useState } from 'react';
import { isAfter, startOfDay } from 'date-fns';
import { isUndefined } from 'lodash';
import { getFirstAvailableDayIndex } from 'domains/consultations/freeConsultationSchedule';
import { getMaxRescheduleDate } from 'domains/appointments/reschedule/calendar';
import { useRescheduleState } from 'domains/direct-reschedule/useRescheduleProvider';
import { RadioChipOptions } from '@expressable/syntax-ds';
import { AvailableTimes } from 'domains/calendars/types';
import { UseInfiniteQueryResult } from 'react-query';
import { Reschedule } from 'domains/add-recurring-sts/types';

type UseRescheduleLogicType = {
  rescheduleInformation: Reschedule | undefined;
  selectedDayIndex: number | undefined;
  currentSlots: Date[] | undefined;
  onChangeDay: (newDayIndex: number) => void;
  dayOptions: {
    value: string;
    date: Date;
    disabled: boolean;
  }[];
  onLoadMoreChipsCallback: (() => Promise<void>) | undefined;
  timeSlot: Date | undefined;
  setTimeSlot: (timeSlot: Date | undefined) => void;
  availableTimesQuery: UseInfiniteQueryResult<AvailableTimes, unknown>;
  clientId: string | undefined;
  rescheduleId: string | undefined;
};

export const useRescheduleLogic = (): UseRescheduleLogicType => {
  const { timeSlot, setTimeSlot, availableTimes, rescheduleInformation, availableTimesQuery, clientId, rescheduleId } =
    useRescheduleState();

  const [selectedDayIndex, setSelectedDayIndex] = useState<number | undefined>();
  const [visibleDays, setVisibleDays] = useState<RadioChipOptions>([]);

  /** @note(juan): this sync is terrible but only way to do it without major refactors to the context
   * This pre-selects the first day with slots available once the initial query is done
   */
  useEffect(() => {
    if (availableTimesQuery.isSuccess && isUndefined(selectedDayIndex) && !timeSlot) {
      setSelectedDayIndex(getFirstAvailableDayIndex(availableTimes));
    } else {
      setSelectedDayIndex(timeSlot ? availableTimes.findIndex(d => d.availableTimes.includes(timeSlot)) : undefined);
    }
  }, [availableTimesQuery.isSuccess]);

  const currentSlots = isUndefined(selectedDayIndex) ? undefined : availableTimes[selectedDayIndex].availableTimes;

  const onChangeDay = useCallback(
    (newDayIndex: number) => {
      setSelectedDayIndex(newDayIndex);
      setTimeSlot(undefined);
    },
    [setSelectedDayIndex, setTimeSlot],
  );

  const maxRescheduleDay =
    rescheduleInformation && getMaxRescheduleDate(new Date(rescheduleInformation.originalAcuityAppointment.datetime));

  const dayOptions = useMemo(() => {
    return (
      availableTimes
        ?.filter(d => !isAfter(startOfDay(d.day), startOfDay(maxRescheduleDay as Date)))
        .map((d, index) => ({
          value: `${index}`,
          date: d.day,
          disabled: d.availableTimes.length === 0,
        })) ?? []
    );
  }, [availableTimes, maxRescheduleDay]);

  const lastAvailableTimesDay = availableTimes[availableTimes.length - 1];

  const isLoadingChipsAllowed = isAfter(startOfDay(maxRescheduleDay as Date), startOfDay(lastAvailableTimesDay?.day));

  const handleLoadMoreChips = useCallback(async () => {
    if (!availableTimes?.length) return;

    try {
      await availableTimesQuery.fetchNextPage({
        pageParam: availableTimes[availableTimes.length - 1].day.toISOString(),
      });

      setVisibleDays(prev => {
        const newItems = availableTimes.slice(prev.length, prev.length).map((d, index) => ({
          value: `${prev.length + index}`,
          date: d.day,
          disabled: d.availableTimes.length === 0,
        }));
        return [...prev, ...newItems];
      });
    } catch (error) {
      throw new Error('Error loading more days' + error);
    }
  }, [availableTimes, availableTimesQuery, visibleDays]);

  const onLoadMoreChipsCallback = useMemo(
    () => (isLoadingChipsAllowed ? handleLoadMoreChips : undefined),
    [isLoadingChipsAllowed, handleLoadMoreChips],
  );

  return {
    rescheduleInformation,
    selectedDayIndex,
    currentSlots,
    onChangeDay,
    dayOptions,
    onLoadMoreChipsCallback,
    timeSlot,
    setTimeSlot,
    availableTimesQuery,
    clientId,
    rescheduleId,
  };
};
